import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Link,
  CircularProgress,
  Divider,
  IconButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Phone as PhoneIcon,
  Headset as HeadsetIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  LocationOn as LocationOnIcon,
  WhatsApp as WhatsAppIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';
import { ConfigLinksDetailsContext } from '../Context/cofingLinksDetailsContext';
import { isMobile } from 'react-device-detect';

const ContactUs = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const isRTL = i18n.language === 'ar';

  const [workingHours, setWorkingHours] = useState([]);

  useEffect(() => {
    const hoursText = isRTL ? configLinks.workingHoursAr : configLinks.workingHoursEn;
    if (hoursText) {
      const lines = hoursText.split('\n').filter(line => line.trim());
      setWorkingHours(lines);
    }
  }, [configLinks, isRTL]);

  const contactInfo = [
    {
      icon: HeadsetIcon,
      label: t('publicServicesPhone'),
      value: configLinks.publicServicesPhone,
      action: configLinks.publicServicesPhone ? `tel:${configLinks.publicServicesPhone}` : null,
      show: !!configLinks.publicServicesPhone,
    },
    {
      icon: PhoneIcon,
      label: t('phone'),
      value: configLinks.phone,
      action: configLinks.phone ? `tel:${configLinks.phone}` : null,
      show: !!configLinks.phone,
    },
    {
      icon: PrintIcon,
      label: t('fax'),
      value: configLinks.fax,
      action: null,
      show: !!configLinks.fax,
    },
    {
      icon: EmailIcon,
      label: t('email'),
      value: configLinks.email,
      action: configLinks.email ? `mailto:${configLinks.email}` : null,
      show: !!configLinks.email,
    },
    {
      icon: LanguageIcon,
      label: t('website'),
      value: configLinks.webSite,
      action: configLinks.webSite
        ? configLinks.webSite.startsWith('http')
          ? configLinks.webSite
          : `https://${configLinks.webSite}`
        : null,
      show: !!configLinks.webSite,
    },
    {
      icon: LocationOnIcon,
      label: t('address'),
      value: configLinks.address,
      action: null,
      show: !!configLinks.address,
    },
  ].filter(item => item.show);

  const socialLinks = [
    {
      name: 'WhatsApp',
      icon: WhatsAppIcon,
      url: configLinks.whatsapp,
      color: '#25D366',
      show: !!configLinks.whatsapp,
    },
    {
      name: 'Facebook',
      icon: FacebookIcon,
      url: configLinks.facebook,
      color: '#1877F2',
      show: !!configLinks.facebook,
    },
  ].filter(item => item.show);

  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 4, mb: 4 }} dir={isRTL ? 'rtl' : 'ltr'}>

      <Paper
        elevation={3}
        sx={{
          p: isMobile ? 2 : 4,
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          textAlign: 'center',
        }}
      >
        <HeadsetIcon sx={{ fontSize: 50, mb: 1 }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('contactUs')}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          {t('weAreHereToHelp')}
        </Typography>
      </Paper>

      {workingHours.length > 0 && (
        <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 2, backgroundColor: theme.palette.background.paper }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
            {t('workingHours')}
          </Typography>
          <Grid container spacing={1}>
            {workingHours.map((hour, index) => {
              const parts = hour.split(':');
              return (
                <Grid item xs={12} key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      p: 1,
                      backgroundColor: index % 2 === 0 ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                      borderRadius: 1,
                      borderBottom: index < workingHours.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>{parts[0].trim()}</Typography>
                    <Typography color="text.secondary">{parts.slice(1).join(':').trim()}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {contactInfo.length > 0 && (
        <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 2, backgroundColor: theme.palette.background.paper }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
            {t('contactInformation')}
          </Typography>
          <Box>
            {contactInfo.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Box key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: index % 2 === 0 ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                      {item.action ? (
                        <Link
                          href={item.action}
                          target={item.action.startsWith('http') ? '_blank' : '_self'}
                          sx={{
                            display: 'block',
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            direction: 'ltr',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {item.value}
                        </Link>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 600, direction: 'ltr' }}>{item.value}</Typography>
                      )}
                    </Box>
                  </Box>
                  {index < contactInfo.length - 1 && <Divider />}
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {socialLinks.length > 0 && (
        <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 2, backgroundColor: theme.palette.background.paper }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main, textAlign: 'center' }}>
            {t('followUs')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            {socialLinks.map((social, index) => {
              const IconComponent = social.icon;
              return (
                <IconButton
                  key={index}
                  component="a"
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: social.color,
                    color: 'white',
                    '&:hover': { backgroundColor: social.color, opacity: 0.85, transform: 'scale(1.1)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <IconComponent sx={{ fontSize: 28 }} />
                </IconButton>
              );
            })}
          </Box>
        </Paper>
      )}

      {contactInfo.length === 0 && socialLinks.length === 0 && workingHours.length === 0 && (
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <CircularProgress size={50} color="primary" />
          <Typography sx={{ mt: 2 }} color="text.secondary">{t('loading')}</Typography>
        </Paper>
      )}

    </Container>
  );
};

export default ContactUs;
